import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { ArrowLeft, Trash2, Users } from 'lucide-react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useMeter } from '../api/inbox'
import { useTags, type Tag } from '../api/tags'
import { useTemplates, type MessageTemplate } from '../api/templates'
import {
  useAudienceCount,
  useCampaign,
  useDeleteCampaign,
  useSaveCampaign,
  type AudienceCount,
  type Campaign,
  type CampaignInput,
  type CampaignVariable,
} from '../api/campaigns'
import { campaignStatusLabel, campaignStatusVariant } from '../lib/campaigns'
import { hasLinkVariable, sendable } from '../lib/templates'
import { TemplatePreview } from '../components/TemplatePreview'
import { LaunchDialog, ProgressPanel, RecipientList, RunActions, TestDialog } from '../components/CampaignRun'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

// One variable as the form holds it. Both strings are kept so switching the
// source back and forth does not lose what was typed.
type Slot = { source: 'text' | 'contactName'; text: string; fallback: string }

type Form = {
  name: string
  phoneNumberId: string
  templateId: string
  header: Slot | undefined
  body: Slot[]
  button: Slot | undefined
  include: string[]
  exclude: string[]
  scheduled: boolean
  sendAt: string // datetime-local, in the device's time zone
}

const textSlot = (): Slot => ({ source: 'text', text: '', fallback: '' })
const nameSlot = (): Slot => ({ source: 'contactName', text: '', fallback: 'there' })

function fromVariable(v: CampaignVariable | undefined): Slot | undefined {
  if (!v) return undefined
  return { source: v.source, text: v.text ?? '', fallback: v.fallback ?? '' }
}

function toVariable(s: Slot): CampaignVariable {
  return s.source === 'text' ? { source: 'text', text: s.text.trim() } : { source: 'contactName', fallback: s.fallback.trim() }
}

// Slots for a newly chosen template. {{1}} of the text is most often the
// customer's name, so it starts as that; everything else as fixed text.
function slotsFor(t: MessageTemplate): Pick<Form, 'header' | 'body' | 'button'> {
  return {
    header: t.headerVariable ? textSlot() : undefined,
    body: Array.from({ length: t.bodyVariables }, (_, i) => (i === 0 ? nameSlot() : textSlot())),
    button: hasLinkVariable(t) ? textSlot() : undefined,
  }
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

function initialForm(c: Campaign | undefined): Form {
  if (!c) {
    return { name: '', phoneNumberId: '', templateId: '', header: undefined, body: [], button: undefined, include: [], exclude: [], scheduled: false, sendAt: '' }
  }
  return {
    name: c.name,
    phoneNumberId: c.phoneNumberId,
    templateId: c.template.id,
    header: fromVariable(c.variables.header),
    body: c.variables.body.map((v) => fromVariable(v) ?? textSlot()),
    button: fromVariable(c.variables.button),
    include: c.includeTags.map((t) => t.id),
    exclude: c.excludeTags.map((t) => t.id),
    scheduled: c.sendAt !== undefined,
    sendAt: c.sendAt ? toLocalInput(c.sendAt) : '',
  }
}

// /campaigns/new and /campaigns/:campaignId. Owners and admins edit drafts;
// a viewer, or a campaign past draft, gets the same page read-only.
export function CampaignEditorPage() {
  const { campaignId } = useParams()
  const campaign = useCampaign(campaignId)
  const me = useMe()
  const lib = useTemplates()
  const tags = useTags()
  const meter = useMeter()

  const loading = (campaignId !== undefined && campaign.isPending) || lib.isPending || tags.isPending || meter.isPending || me.isPending
  const failed = campaign.error ?? lib.error ?? tags.error ?? meter.error ?? me.error

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 sm:py-8">
      <Link to="/campaigns" className="flex items-center gap-1.5 self-start text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" />
        Campaigns
      </Link>
      {failed ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(failed as RequestFailed).message}</p>
      ) : loading || !lib.data || !tags.data || !meter.data || !me.data ? (
        <div role="status" aria-label="Loading campaign" className="flex flex-col gap-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <CampaignForm
          key={campaign.data?.id ?? 'new'}
          campaign={campaign.data}
          templates={lib.data.templates}
          tags={tags.data}
          numbers={meter.data.filter((n) => n.numberStatus !== 'disconnected')}
          canEdit={me.data.user.role === 'owner' || me.data.user.role === 'admin'}
        />
      )}
    </main>
  )
}

type FormProps = {
  campaign: Campaign | undefined
  templates: MessageTemplate[]
  tags: Tag[]
  numbers: { phoneNumberId: string; displayNumber: string }[]
  canEdit: boolean
}

function CampaignForm({ campaign, templates, tags, numbers, canEdit }: FormProps) {
  const navigate = useNavigate()
  const save = useSaveCampaign(campaign?.id)
  const remove = useDeleteCampaign()
  const [form, setForm] = useState<Form>(() => {
    const f = initialForm(campaign)
    if (!f.phoneNumberId && numbers.length === 1 && numbers[0]) f.phoneNumberId = numbers[0].phoneNumberId
    return f
  })
  // A new draft remounts under its id after the first save; the confirmation
  // travels with the navigation.
  const location = useLocation()
  const [saved, setSaved] = useState((location.state as { saved?: boolean } | null)?.saved === true)
  // Launch and test use the saved draft, so they wait for unsaved edits.
  const [dirty, setDirty] = useState(false)
  const editable = canEdit && (campaign === undefined || campaign.status === 'draft')
  // Any edit clears the last save's outcome, so a fixed field stops showing its error.
  const set = (patch: Partial<Form>) => {
    setForm((f) => ({ ...f, ...patch }))
    setSaved(false)
    setDirty(true)
    save.reset()
  }

  const options = templates.filter(sendable)
  // A saved campaign's template stays visible even if it stopped being sendable.
  const t = templates.find((x) => x.id === form.templateId)
  const audience = { includeTagIds: form.include, excludeTagIds: form.exclude }
  const live = useAudienceCount(audience, editable)
  const count: AudienceCount | undefined = editable ? live.data : campaign?.audience

  const error = save.error as RequestFailed | null
  const fieldError = (k: string) => error?.fields?.[k]
  const formError = error && !error.fields ? error.message : undefined

  if (editable && numbers.length === 0) {
    return <Empty>No WhatsApp number is connected, so there is nothing to send a campaign from. An owner can connect one.</Empty>
  }
  if (editable && !campaign && options.length === 0) {
    return (
      <Empty>
        No approved templates yet. Campaigns send an approved template: make or sync one on the{' '}
        <Link to="/templates" className="font-semibold text-primary-text underline underline-offset-2">Templates</Link> page first.
      </Empty>
    )
  }

  const submit = () => {
    if (!editable || save.isPending) return
    const body: CampaignInput = {
      name: form.name,
      phoneNumberId: form.phoneNumberId,
      templateId: form.templateId,
      variables: {
        body: form.body.map(toVariable),
        ...(form.header ? { header: toVariable(form.header) } : {}),
        ...(form.button ? { button: toVariable(form.button) } : {}),
      },
      audience,
      ...(form.scheduled && form.sendAt ? { sendAt: new Date(form.sendAt).toISOString() } : {}),
    }
    save.mutate(body, {
      onSuccess: (c) => {
        setSaved(true)
        setDirty(false)
        if (!campaign) void navigate(`/campaigns/${c.id}`, { replace: true, state: { saved: true } })
      },
    })
  }
  const missing = !form.name.trim() || !form.phoneNumberId || !form.templateId || (form.scheduled && !form.sendAt)

  // The preview shows each variable as a contact without a name would see it.
  const example = (s: Slot | undefined) => (s ? (s.source === 'text' ? s.text : s.fallback) : '')

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="min-w-0 flex-[1_1_240px] text-2xl font-bold [overflow-wrap:anywhere]">{campaign ? campaign.name : 'New campaign'}</h1>
        {campaign && <Badge variant={campaignStatusVariant[campaign.status]}>{campaignStatusLabel[campaign.status]}</Badge>}
      </div>
      {!editable && (
        <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
          {!canEdit
            ? 'You can read campaigns. Owners and admins create and change them.'
            : campaign?.status === 'scheduled'
              ? 'A scheduled campaign cannot be changed. Move it back to draft to edit it.'
              : 'This campaign has been launched, so it can no longer be changed.'}
        </p>
      )}

      <form
        className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <fieldset disabled={!editable} className="flex min-w-0 flex-col gap-5">
          <Section title="Campaign">
            <Field id="c-name" label="Name, for your team" error={fieldError('name')}>
              <Input id="c-name" value={form.name} maxLength={200} placeholder="Diwali offer" onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field id="c-number" label="Sent from" error={fieldError('phoneNumberId')}>
              {numbers.length === 1 && numbers[0] && form.phoneNumberId === numbers[0].phoneNumberId ? (
                <p id="c-number" className="text-sm">{numbers[0].displayNumber}</p>
              ) : (
                <Select value={form.phoneNumberId} onValueChange={(v) => set({ phoneNumberId: v })} disabled={!editable}>
                  <SelectTrigger id="c-number" className="w-full"><SelectValue placeholder="Choose a number" /></SelectTrigger>
                  <SelectContent>
                    {numbers.map((n) => <SelectItem key={n.phoneNumberId} value={n.phoneNumberId}>{n.displayNumber}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field id="c-template" label="Template" error={fieldError('templateId')}>
              <Select
                value={form.templateId}
                disabled={!editable}
                onValueChange={(id) => {
                  const next = templates.find((x) => x.id === id)
                  if (next) set({ templateId: id, ...slotsFor(next) })
                }}
              >
                <SelectTrigger id="c-template" className="w-full"><SelectValue placeholder="Choose an approved template" /></SelectTrigger>
                <SelectContent>
                  {(t && !options.includes(t) ? [t, ...options] : options).map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name} ({o.language}, {o.category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {t && (form.header || form.body.length > 0 || form.button) && (
            <Section title="Fill the variables" hint="Each one is the same text for everyone, or the contact's name.">
              {form.header && (
                <SlotField id="v-header" label="Header {{1}}" slot={form.header} onChange={(s) => set({ header: s })} error={fieldError('variables.header')} />
              )}
              {form.body.map((s, i) => (
                <SlotField
                  key={i}
                  id={`v-body-${i}`}
                  label={`{{${i + 1}}}`}
                  slot={s}
                  onChange={(x) => set({ body: form.body.map((y, j) => (j === i ? x : y)) })}
                />
              ))}
              {fieldError('variables.body') && <p className="text-sm text-destructive">{fieldError('variables.body')}</p>}
              {form.button && (
                <SlotField id="v-button" label="End of the link, {{1}}" slot={form.button} onChange={(s) => set({ button: s })} error={fieldError('variables.button')} />
              )}
            </Section>
          )}

          <Section title="Who gets it" hint="Contacts with any of the chosen tags. Choose none to send to every contact.">
            {tags.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No tags yet, so the campaign goes to every contact. Tag contacts on the Contacts page, or when importing a CSV, to send to some of them.
              </p>
            ) : (
              <>
                <TagPicker
                  legend="Send to contacts tagged"
                  tags={tags}
                  chosen={form.include}
                  blocked={form.exclude}
                  onChange={(include) => set({ include })}
                />
                <TagPicker
                  legend="Leave out contacts tagged"
                  tags={tags}
                  chosen={form.exclude}
                  blocked={form.include}
                  onChange={(exclude) => set({ exclude })}
                />
              </>
            )}
            {fieldError('audience') && <p className="text-sm text-destructive">{fieldError('audience')}</p>}
          </Section>

          <Section title="When">
            <div className="flex items-center gap-3">
              <Switch id="c-schedule" checked={form.scheduled} onCheckedChange={(on) => set({ scheduled: on })} />
              <Label htmlFor="c-schedule">Send at a set time</Label>
            </div>
            {form.scheduled ? (
              <Field id="c-send-at" label="Date and time, in this device's time zone" error={fieldError('sendAt')}>
                <Input id="c-send-at" type="datetime-local" value={form.sendAt} onChange={(e) => set({ sendAt: e.target.value })} className="w-full sm:w-64" />
              </Field>
            ) : (
              <p className="text-sm text-muted-foreground">It goes out as soon as it is launched.</p>
            )}
          </Section>
        </fieldset>

        {/* Once launched, progress is what the page is for: first on narrow screens. */}
        <aside className={cn('flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start', campaign && campaign.status !== 'draft' && 'order-first lg:order-none')}>
          {campaign && campaign.status !== 'draft' ? (
            <>
              <ProgressPanel campaign={campaign} />
              {canEdit && <RunActions campaign={campaign} />}
            </>
          ) : (
            <AudiencePanel count={count} loading={editable && live.isPending} error={editable ? (live.error as RequestFailed | null) : null} />
          )}
          {t && (
            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold">Preview</h2>
              <TemplatePreview
                header={t.header}
                headerExample={example(form.header)}
                body={t.body}
                bodyExamples={form.body.map(example)}
                footer={t.footer}
                buttons={t.buttons}
                className="p-3"
              />
              {form.body.some((s) => s.source === 'contactName') || form.header?.source === 'contactName' ? (
                <p className="text-xs text-muted-foreground">Shown as a contact without a name sees it. Others see their name.</p>
              ) : null}
              {t.category === 'marketing' && (
                <p className="text-xs text-muted-foreground">A marketing template: Meta charges its marketing rate for each one delivered.</p>
              )}
            </div>
          )}
          {editable && (
            <div className="flex flex-col gap-2">
              {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
              {error?.fields && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
              {saved && !save.isPending && <p role="status" className="text-sm text-primary-text">Draft saved.</p>}
              <Button type="submit" variant={campaign && !dirty ? 'outline' : 'default'} disabled={missing || save.isPending}>
                {save.isPending ? 'Saving' : 'Save draft'}
              </Button>
              {campaign && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <TestDialog campaign={campaign} disabled={dirty} />
                    <LaunchDialog campaign={campaign} disabled={dirty} />
                  </div>
                  {dirty && <p className="text-xs text-muted-foreground">Save your changes before testing or launching.</p>}
                </>
              )}
              {campaign && <DeleteDraft name={campaign.name} pending={remove.isPending} error={remove.error as RequestFailed | null} onDelete={() => remove.mutate(campaign.id, { onSuccess: () => void navigate('/campaigns') })} />}
            </div>
          )}
        </aside>
      </form>
      {campaign?.progress && <RecipientList campaign={campaign} />}
    </div>
  )
}

function AudiencePanel({ count, loading, error }: { count: AudienceCount | undefined; loading: boolean; error: RequestFailed | null }) {
  return (
    <section aria-labelledby="audience-h" className="flex flex-col gap-2 rounded-xl border bg-card p-4" aria-live="polite">
      <h2 id="audience-h" className="flex items-center gap-2 text-sm font-semibold">
        <Users className="size-4" aria-hidden="true" />
        Audience
      </h2>
      {error ? (
        <p role="alert" className="text-sm text-destructive">{error.message}</p>
      ) : loading || !count ? (
        <Skeleton className="h-12 w-full" />
      ) : (
        <>
          <p>
            <span className="text-2xl font-bold">{count.eligible.toLocaleString('en-IN')}</span>{' '}
            <span className="text-sm text-muted-foreground">of {count.matching.toLocaleString('en-IN')} matching contacts will get it</span>
          </p>
          {count.matching > count.eligible && (
            <ul className="flex flex-col gap-0.5 text-sm text-muted-foreground">
              {count.notOptedIn > 0 && <li>{count.notOptedIn.toLocaleString('en-IN')} have not agreed to receive messages</li>}
              {count.doNotMessage > 0 && <li>{count.doNotMessage.toLocaleString('en-IN')} are on do-not-message</li>}
              {count.noPhone > 0 && <li>{count.noPhone.toLocaleString('en-IN')} have no phone number on file</li>}
            </ul>
          )}
          {count.matching === 0 && <p className="text-sm text-muted-foreground">No contacts match these tags.</p>}
        </>
      )}
    </section>
  )
}

function TagPicker({ legend, tags, chosen, blocked, onChange }: { legend: string; tags: Tag[]; chosen: string[]; blocked: string[]; onChange: (ids: string[]) => void }) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const id = `${legend}-${tag.id}`
          const on = chosen.includes(tag.id)
          return (
            <label
              key={tag.id}
              htmlFor={id}
              className="relative flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 sm:min-h-9"
            >
              <Checkbox
                id={id}
                checked={on}
                disabled={!on && blocked.includes(tag.id)}
                onCheckedChange={(c) => onChange(c === true ? [...chosen, tag.id] : chosen.filter((x) => x !== tag.id))}
              />
              {tag.name}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

function SlotField({ id, label, slot, onChange, error }: { id: string; label: string; slot: Slot; onChange: (s: Slot) => void; error?: string | undefined }) {
  const byName = slot.source === 'contactName'
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${id}-value`}>{label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={slot.source} onValueChange={(v) => onChange({ ...slot, source: v === 'contactName' ? 'contactName' : 'text' })}>
          <SelectTrigger aria-label={`How to fill ${label}`} className="w-full sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="contactName">Contact's name</SelectItem>
            <SelectItem value="text">Same text for all</SelectItem>
          </SelectContent>
        </Select>
        <Input
          id={`${id}-value`}
          aria-label={byName ? `${label}: when a contact has no name, use` : `${label}: text`}
          value={byName ? slot.fallback : slot.text}
          maxLength={byName ? 60 : 1024}
          placeholder={byName ? 'If no name: there' : 'Text for everyone'}
          onChange={(e) => onChange(byName ? { ...slot, fallback: e.target.value } : { ...slot, text: e.target.value })}
          className="min-w-0 flex-1"
        />
      </div>
      {byName && <p className="text-xs text-muted-foreground">Contacts without a name get the text in the box.</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

function DeleteDraft({ name, pending, error, onDelete }: { name: string; pending: boolean; error: RequestFailed | null; onDelete: () => void }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" className="text-destructive">
          <Trash2 aria-hidden="true" />
          Delete draft
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {name}?</DialogTitle>
          <DialogDescription>The draft is removed for the whole team. Nothing has been sent, so no customer notices.</DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">Keep it</Button>
          </DialogClose>
          <Button type="button" variant="destructive" disabled={pending} onClick={onDelete}>{pending ? 'Deleting' : 'Delete draft'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="relative flex flex-col gap-4 rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-0.5">
        <h2 className="font-semibold">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function Field({ id, label, error, children }: { id: string; label: string; error: string | undefined; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">{children}</p>
}
